import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { adminFetchUsers, adminToggleUserActivation, adminVerifyUser } from '../../store/slices/adminSlice';
import { pushToast } from '../../store/slices/toastSlice';
import Modal from '../../components/common/Modal';

const roleOptions = [ '', 'WHS', 'RTL', 'ADMIN' ];
const statusOptions = [ '', 'UNVERIFIED', 'PENDING', 'APPROVED', 'REJECTED' ];

function AdminUsers() {
  const dispatch = useDispatch();
  const { users, isLoading, userPagination } = useSelector(s => s.admin);
  const [filters, setFilters] = useState({ search: '', role: '', status: '', active: '' });
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const searchRef = useRef();
  const [sort, setSort] = useState({ field: 'companyName', dir: 'asc' });
  const [verifyModal, setVerifyModal] = useState({ open: false, userId: null, status: 'APPROVED', notes: '' });
  const [pendingIds, setPendingIds] = useState(new Set());

  const sortedUsers = React.useMemo(() => {
    const cloned = [...users];
    const { field, dir } = sort;
    cloned.sort((a,b) => {
      const av = (a[field] || (a.verification && a.verification.status) || '').toString().toLowerCase();
      const bv = (b[field] || (b.verification && b.verification.status) || '').toString().toLowerCase();
      if (av < bv) return dir === 'asc' ? -1 : 1;
      if (av > bv) return dir === 'asc' ? 1 : -1;
      return 0;
    });
    return cloned;
  }, [users, sort]);

  const load = useCallback(() => {
    dispatch(adminFetchUsers({ ...filters, search: debouncedSearch, page: userPagination.page }))
      .unwrap()
      .catch(err => dispatch(pushToast({ type:'error', message: err || 'Failed to load users'})));
  }, [dispatch, filters, debouncedSearch, userPagination.page]);

  useEffect(() => { load(); }, [load]);

  const onChangeFilter = (e) => {
    const { name, value } = e.target;
    if (name === 'search') {
      setFilters(f => ({ ...f, search: value }));
    } else {
      setFilters(f => ({ ...f, [name]: value }));
    }
  };

  // Debounce search
  useEffect(() => {
    const id = setTimeout(() => setDebouncedSearch(filters.search), 500);
    return () => clearTimeout(id);
  }, [filters.search]);

  const onSubmitFilters = (e) => { e.preventDefault(); dispatch(adminFetchUsers({ ...filters, search: debouncedSearch, page: 1 })); };

  const toggleActivation = (user) => {
    setPendingIds(p => new Set(p).add(user._id));
    dispatch(adminToggleUserActivation({ id: user._id, isActive: !user.isActive }))
      .unwrap()
      .then(() => dispatch(pushToast({ type:'success', message: `User ${user.companyName} ${user.isActive ? 'deactivated' : 'activated'}` })))
      .catch(err => dispatch(pushToast({ type:'error', message: err || 'Activation toggle failed'})))
      .finally(() => setPendingIds(p => { const cp = new Set(p); cp.delete(user._id); return cp; }));
  };

  const openVerify = (user) => setVerifyModal({ open: true, userId: user._id, status: 'APPROVED', notes: '' });
  const closeVerify = () => setVerifyModal({ open: false, userId: null, status: 'APPROVED', notes: '' });
  const submitVerify = () => {
    if (!verifyModal.userId) return;
    setPendingIds(p => new Set(p).add(verifyModal.userId));
    dispatch(adminVerifyUser({ id: verifyModal.userId, status: verifyModal.status, notes: verifyModal.notes }))
      .unwrap()
      .then(() => dispatch(pushToast({ type:'success', message: `User verification ${verifyModal.status}` })))
      .catch(err => dispatch(pushToast({ type:'error', message: err || 'Verification failed'})))
      .finally(() => {
        setPendingIds(p => { const cp = new Set(p); cp.delete(verifyModal.userId); return cp; });
        closeVerify();
      });
  };

  const headerClick = (field) => {
    setSort(s => s.field === field ? { field, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { field, dir:'asc' });
  };

  const sortIndicator = (field) => sort.field === field ? (sort.dir === 'asc' ? ' ▲' : ' ▼') : '';

  return (
    <div style={{ padding: '1rem' }}>
      <h2>Admin - Users</h2>
      <form onSubmit={onSubmitFilters} style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
  <input ref={searchRef} name="search" placeholder="Search company/email" value={filters.search} onChange={onChangeFilter} />
        <select name="role" value={filters.role} onChange={onChangeFilter}>
          {roleOptions.map(r => <option key={r} value={r}>{r || 'All Roles'}</option>)}
        </select>
        <select name="status" value={filters.status} onChange={onChangeFilter}>
          {statusOptions.map(s => <option key={s} value={s}>{s || 'All Statuses'}</option>)}
        </select>
        <select name="active" value={filters.active} onChange={onChangeFilter}>
          <option value="">All Active States</option>
          <option value="true">Active</option>
          <option value="false">Inactive</option>
        </select>
        <button type="submit">Apply</button>
      </form>

      {isLoading && <p>Loading...</p>}
      {!isLoading && users.length === 0 && <p>No users found.</p>}

      {!isLoading && sortedUsers.length > 0 && (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={th} onClick={() => headerClick('companyName')}>Company{sortIndicator('companyName')}</th>
              <th style={th} onClick={() => headerClick('email')}>Email{sortIndicator('email')}</th>
              <th style={th} onClick={() => headerClick('accountType')}>Role{sortIndicator('accountType')}</th>
              <th style={th} onClick={() => headerClick('isActive')}>Active{sortIndicator('isActive')}</th>
              <th style={th} onClick={() => headerClick('verification')}>Verification{sortIndicator('verification')}</th>
              <th style={th}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {sortedUsers.map(u => (
              <tr key={u._id} style={{ borderBottom: '1px solid #ddd' }}>
                <td style={td}>{u.companyName}</td>
                <td style={td}>{u.email}</td>
                <td style={td}>{u.accountType}</td>
                <td style={td}>{u.isActive ? 'Yes' : 'No'}</td>
                <td style={td}>
                  {u.verification?.status}
                  {u.verification?.reviewedAt && (
                    <div style={{ fontSize:'0.7rem', color:'#555' }}>
                      {u.verification.reviewedBy ? 'by reviewer' : ''} {new Date(u.verification.reviewedAt).toLocaleDateString()}
                    </div>
                  )}
                </td>
                <td style={td}>
                  <button disabled={pendingIds.has(u._id)} type="button" onClick={() => toggleActivation(u)}>{pendingIds.has(u._id) ? '...' : (u.isActive ? 'Deactivate' : 'Activate')}</button>{' '}
                  <button disabled={pendingIds.has(u._id)} type="button" onClick={() => openVerify(u)}>Verify/Reject</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <div style={{ marginTop: '0.75rem' }}>
        <button type="button" disabled={userPagination.page <= 1} onClick={() => dispatch(adminFetchUsers({ ...filters, page: userPagination.page - 1 }))}>Prev</button>{' '}
        <span>Page {userPagination.page} / {userPagination.totalPages}</span>{' '}
        <button type="button" disabled={userPagination.page >= userPagination.totalPages} onClick={() => dispatch(adminFetchUsers({ ...filters, page: userPagination.page + 1 }))}>Next</button>
      </div>

      <Modal
        open={verifyModal.open}
        onClose={closeVerify}
        title="Verify / Reject User"
        footer={(
          <>
            <button disabled={pendingIds.has(verifyModal.userId)} onClick={submitVerify}>Submit</button>
            <button onClick={closeVerify}>Cancel</button>
          </>
        )}
      >
        <div style={{ display:'flex', flexDirection:'column', gap:'0.75rem' }}>
          <label style={{ display:'flex', flexDirection:'column', gap:'0.25rem' }}>Status
            <select value={verifyModal.status} onChange={e => setVerifyModal(v => ({ ...v, status: e.target.value }))}>
              <option value="APPROVED">APPROVED</option>
              <option value="REJECTED">REJECTED</option>
            </select>
          </label>
          <label style={{ display:'flex', flexDirection:'column', gap:'0.25rem' }}>Notes
            <textarea rows={4} value={verifyModal.notes} onChange={e => setVerifyModal(v => ({ ...v, notes: e.target.value }))} />
          </label>
        </div>
      </Modal>
    </div>
  );
}

const th = { textAlign: 'left', borderBottom: '2px solid #ccc', padding: '4px' };
const td = { padding: '4px' };
// removed inline modal styles (using Modal component)

export default AdminUsers;
